import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@utils/cn';
import { ScheduleRibbon } from '../ribbon';
import { PersonCardProps } from './types';

export const PersonCard = ({
  name,
  subtitle,
  avatar,
  selected,
  conflict,
  schedules,
  currentSchedule,
  onSelect,
  disabled,
  footer,
  nameAddon,
}: PersonCardProps) => {
  const { t } = useTranslation('app');
  const items = [
    ...schedules.map((schedule) => ({ schedule, current: false })),
    ...(selected && currentSchedule ? [{ schedule: currentSchedule, current: true }] : []),
  ].sort((a, b) => a.schedule.startsAt.localeCompare(b.schedule.startsAt));

  return (
    <div
      className={cn(
        'rounded-lg border border-neutral-200 bg-white',
        selected && 'border-primary-500 shadow-[inset_0_0_0_1px_theme(colors.primary.500)]',
        selected &&
          conflict &&
          'border-warning-500 shadow-[inset_0_0_0_1px_theme(colors.warning.500)]',
      )}>
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={disabled}
        onClick={onSelect}
        className="grid min-h-16 w-full grid-cols-[24px_36px_minmax(0,1fr)] items-center gap-x-2.5 rounded-lg px-3 py-2.5 text-left disabled:cursor-not-allowed">
        <span
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded-full border-2 border-neutral-300',
            selected &&
              'border-primary-500 after:block after:h-3 after:w-3 after:rounded-full after:bg-primary-500',
          )}
        />
        {avatar}
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5">
            <b className="min-w-0 text-[15px] font-semibold text-neutral-800 [overflow-wrap:anywhere]">
              {name}
            </b>
            {nameAddon ||
              (selected && conflict && (
                <AlertTriangle
                  role="img"
                  aria-label={t('schedule.conflicts')}
                  className="h-3.5 w-3.5 shrink-0 text-warning-500"
                />
              ))}
          </span>
          {subtitle && <small className="text-[13px] text-neutral-500">{subtitle}</small>}
        </span>
      </button>
      {footer && <div className="-mt-1.5 pb-2.5 pl-[82px] pr-3">{footer}</div>}
      {Boolean(items.length) && (
        <div className="flex flex-col gap-1.5 pb-3 pl-[82px] pr-3">
          {items.map(({ schedule, current }) => (
            <ScheduleRibbon
              key={current ? 'current' : schedule.id}
              schedule={schedule}
              current={current}
            />
          ))}
        </div>
      )}
    </div>
  );
};
